import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import ProductWithDetails from "@/product/common/ProductWithDetails"
import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { ProductFilterDropdownContentQuery } from "@components/dropdown/product/__generated__/ProductFilterDropdownContentQuery.graphql"
import { DiscoIcon, SelectOption } from "@disco-ui"
import DiscoPaginatedDropdownContent, {
  DiscoPaginatedDropdownContentSkeleton,
} from "@disco-ui/dropdown/DiscoPaginatedDropdownContent"
import { ArrayUtils } from "@utils/array/arrayUtils"
import { TestIDProps } from "@utils/typeUtils"
import { useMemo } from "react"
import { graphql } from "relay-runtime"

const PRODUCTS_PER_LOAD = 10

type ProductFilterOption = SelectOption<GlobalID | "all" | "community">

export type ProductFilterDropdownContentState = {
  allProducts: ProductFilterOption | null
  allCommunity?: ProductFilterOption | null
  products: ProductFilterOption[]
}

export interface ProductFilterDropdownContentProps extends TestIDProps {
  filterState: ProductFilterDropdownContentState
  setFilterState: (state: ProductFilterDropdownContentState) => void
  hideCommunityOption?: boolean
  multiSelect?: boolean
  onOptionSelect?: (option: ProductFilterOption | null) => void
  filterByAdminAccess?: boolean
  viewerIsMember?: boolean
}

function ProductFilterDropdownContent({
  testid = "ProductFilterDropdownContent",
  filterState,
  setFilterState,
  hideCommunityOption = false,
  multiSelect = false,
  onOptionSelect,
  filterByAdminAccess,
  viewerIsMember,
}: ProductFilterDropdownContentProps) {
  const activeOrganization = useActiveOrganization()
  const classes = useStyles()
  const selectedOptions = useMemo(() => {
    return [
      ...(filterState.allProducts ? [filterState.allProducts] : []),
      ...(filterState.allCommunity ? [filterState.allCommunity] : []),
      ...filterState.products,
    ]
  }, [filterState.allProducts, filterState.allCommunity, filterState.products])
  const selectedOptionsByValue = useMemo(
    () => ArrayUtils.mapBy(selectedOptions, "value"),
    [selectedOptions]
  )

  const { data, pagination } =
    Relay.useRefetchablePaginationQuery<ProductFilterDropdownContentQuery>(
      graphql`
        query ProductFilterDropdownContentQuery(
          $id: ID!
          $inProductsViewerIsAdminOf: Boolean
          $viewerIsMember: Boolean
          $first: Int!
          $search: String
          $after: String
        ) {
          organization: node(id: $id) {
            ... on Organization {
              products(
                first: $first
                after: $after
                search: $search
                type: "course"
                filterByAdminAccess: $inProductsViewerIsAdminOf
                viewerIsMember: $viewerIsMember
              ) @connection(key: "ProductFilterDropdownContent__products") {
                edges {
                  node {
                    id
                    name
                    ...ProductWithDetailsFragment
                  }
                }
                pageInfo {
                  startCursor
                  endCursor
                  hasNextPage
                  hasPreviousPage
                }
              }
            }
          }
        }
      `,
      {
        id: activeOrganization?.id,
        first: PRODUCTS_PER_LOAD,
        inProductsViewerIsAdminOf:
          typeof filterByAdminAccess === "boolean"
            ? filterByAdminAccess
            : activeOrganization?.viewerIsOwnerOrAdmin,
        viewerIsMember,
      },
      { connectionName: "organization.products", fetchPolicy: "network-only" }
    )

  const organization = data?.organization

  const products = Relay.connectionToArray(organization?.products)
  const productsById = useMemo(() => ArrayUtils.mapBy(products, "id"), [products])
  const options = [
    { value: "all", title: "All", testid: "product-all" },
    ...ArrayUtils.spreadIf(
      {
        value: "community",
        title: "Community",
        testid: "product-community",
      },
      !hideCommunityOption
    ),
    ...products.map((product) => ({
      value: product.id,
      title: product.name,
      testid: `product-${product.name}`,
    })),
  ]

  if (!organization) return null

  return (
    <DiscoPaginatedDropdownContent
      testid={testid}
      pagination={pagination}
      selectedOptions={selectedOptions}
      onOptionSelect={handleSelect}
      options={options?.map((option) => {
        const product = option.value ? productsById[option.value] : null
        if (!product) return option

        return {
          ...option,
          children: (
            <div className={classes.content}>
              <ProductWithDetails productKey={product} testid={testid} />

              {selectedOptionsByValue[option.value] && <DiscoIcon icon={"check"} />}
            </div>
          ),
        }
      })}
    />
  )

  function handleSelect(option: ProductFilterOption | null) {
    const state: ProductFilterDropdownContentState = {
      allProducts: null,
      allCommunity: null,
      products: [],
    }

    if (option?.value === "all") {
      state.allProducts = option
    } else if (option?.value === "community") {
      state.allCommunity = option
    } else if (option) {
      state.products = multiSelect
        ? filterState.products?.concat(option) ?? [option]
        : [option]
    }

    setFilterState(state)
    onOptionSelect?.(option)
  }
}

const useStyles = makeUseStyles((theme) => ({
  content: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    width: "100%",
  },
}))

export const ProductFilterDropdownContentSkeleton = () => {
  return <DiscoPaginatedDropdownContentSkeleton />
}

export default Relay.withSkeleton({
  component: ProductFilterDropdownContent,
  skeleton: ProductFilterDropdownContentSkeleton,
})
